import { NavigateFunction } from 'react-router-dom'

import { confirmIconv2 } from '@/assets/icons'
import Divider from '@/components/atoms/divider/divider'
import PosReceiptCard from '@/components/molecules/posReceiptCard/posReceiptCard'
import SessionCounter from '@/components/molecules/sessionCounter/sessionCounter'
import { VoucherConfirmation } from '@/data/entities/voucher-confirmation'
import { useNavigationContext } from '@/hooks/useNavigationHook'
import { useTranslate } from '@/i18n/useTranslate'
import { PrintSlipRequest } from '@/utilities/posSlipHelper'

export default function VoucherConfirmationTemplate({
    voucherConfirmation,
    navigate,
    onPrintVoucher,
    canPrintVoucher = true,
    posSlip = null,
    sessionId,
    onComplete: onCompleteOverride
}: {
    voucherConfirmation: VoucherConfirmation
    navigate: NavigateFunction
    onPrintVoucher?: () => void
    canPrintVoucher?: boolean
    posSlip?: PrintSlipRequest | null
    sessionId?: string
    onComplete?: () => void
}) {
    const { t } = useTranslate()
    const { startUrl } = useNavigationContext()

    const dateTime = [voucherConfirmation?.date, voucherConfirmation?.time]
        .filter(Boolean)
        .join(' u ')

    const infoRows = [
        {
            label: t('voucherConfirmation.dateTime'),
            value: dateTime
        },
        {
            label: t('voucherGenerated.referenceNo'),
            value: voucherConfirmation?.referenceNo
        },
        {
            label: t('voucherGenerated.terminal'),
            value: voucherConfirmation?.terminal
        }
    ]

    const onComplete = () => {
        if (onCompleteOverride) {
            onCompleteOverride()
            return
        }
        navigate(startUrl ?? '/welcome')
    }

    const qrUrl = voucherConfirmation?.voucherCode
        ? `https://vqr-generator.fly.dev/api/vcash-print?data=https://voucher.vcash.rs?code=${voucherConfirmation.voucherCode}`
        : null

    return (
        <div className="voucher-confirmation">
            <img
                src={confirmIconv2}
                alt="Confirmed icon"
                className="confirmed-icon"
            />
            <h1>{t('voucherConfirmation.title')}</h1>

            <div className="invoice-content">
                <div className="invoice-content__info">
                    <div className="voucher-confirmation__info-table">
                        {infoRows.map((row) => (
                            <div
                                key={row.label}
                                className="voucher-confirmation__info-row">
                                <p className="voucher-confirmation__info-label">
                                    {row.label}:
                                </p>
                                <p className="voucher-confirmation__info-value">
                                    {row.value}
                                </p>
                            </div>
                        ))}
                    </div>
                    <Divider />
                    <div className="voucher-confirmation__info-row voucher-confirmation__info-row--amount">
                        <p className="voucher-confirmation__info-label">
                            {t('voucherGenerated.amount')}:
                        </p>
                        <p className="voucher-confirmation__info-value">
                            {voucherConfirmation?.amount}
                        </p>
                    </div>
                </div>

                {voucherConfirmation?.voucherCode && (
                    <div className="invoice-content__qr-box">
                        <p className="invoice-content__qr-label">
                            {t('home.code.activationCode')}:
                        </p>
                        <p className="invoice-content__qr-code">
                            {voucherConfirmation.voucherCode}
                        </p>
                        {qrUrl && (
                            <img
                                className="invoice-content__qr-image"
                                src={qrUrl}
                                alt="QR Code"
                            />
                        )}
                    </div>
                )}
            </div>

            {posSlip && (
                <PosReceiptCard slip={posSlip} sessionId={sessionId} />
            )}

            <div className="voucher-confirmation__bottom-actions">
                <SessionCounter onEndSession={() => navigate(startUrl ?? '/')} />
            </div>

            <div
                className={`voucher-confirmation__action-wrapper${
                    canPrintVoucher
                        ? ''
                        : ' voucher-confirmation__action-wrapper--single'
                }`}>
                <button
                    type="button"
                    className="voucher-confirmation__finish-button"
                    onClick={onComplete}>
                    {t('voucherGenerated.btnFinish')}
                </button>
                {canPrintVoucher && (
                    <button
                        type="button"
                        className="voucher-confirmation__print-button"
                        onClick={() => onPrintVoucher?.()}>
                        {t('voucherGenerated.printVoucher')}
                    </button>
                )}
            </div>
        </div>
    )
}
